import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";
import { SEO } from "@/components/SEO";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { LogIn, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";

const Maintenance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  const [content, setContent] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "maintenance_mode")
        .maybeSingle();

      if (error || !data) {
        console.error("Error loading maintenance content:", error);
        setLoading(false);
        return;
      }

      const modeData = data.value as { title?: string; content?: string };
      
      setContent({
        title: modeData.title || "Sito in Manutenzione",
        content: modeData.content || "<p>Stiamo effettuando lavori di manutenzione.</p>"
      });
      setLoading(false);
    };

    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <>
      <SEO title={content.title} description="Sito in manutenzione" />
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-card border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark p-8">
            <div
              className="prose prose-lg max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(content.content, {
                  allowedTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'pre', 'code', 'br', 'div', 'span', 'iframe'],
                  allowedAttributes: ['href', 'src', 'alt', 'class', 'style', 'width', 'height', 'frameborder', 'allowfullscreen']
                })
              }}
            />
            <div className="mt-6 flex flex-col items-center gap-4">
              {user && !isAdmin ? (
                <>
                  <p className="text-sm text-muted-foreground text-center">
                    {t("maintenance.loggedInMessage")}
                  </p>
                  <div className="flex gap-3">
                    <BrutalButton
                      onClick={() => navigate('/')}
                      variant="primary"
                    >
                      {t("maintenance.backToHome")}
                    </BrutalButton>
                    <BrutalButton
                      onClick={async () => {
                        await signOut();
                        navigate('/auth/login');
                      }}
                      variant="secondary"
                    >
                      <LogOut className="w-5 h-5 mr-2" />
                      {t("auth.signOut")}
                    </BrutalButton>
                  </div>
                </>
              ) : !user ? (
                <BrutalButton
                  onClick={() => navigate('/auth/login?mode=maintenance')}
                  variant="primary"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  {t("maintenance.loginButton")}
                </BrutalButton>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Maintenance;
